// Utils used across tools.

import { ENTITY_ID_PREFIX } from "$lib/utils/constants";
import { Cartesian3, Color, ColorMaterialProperty, ConstantPositionProperty, ConstantProperty, PolylineOutlineMaterialProperty, Transforms, type CzmlDataSource, type Entity, type EntityCollection } from "cesium";

/**
 * Get the parent mastpoint
 * @param entity A child entity which is part of a mastpoint group
 * @returns 
 */
const getGroupRoot = (entity: Entity) => {
    let cur = entity;
    while (cur?.parent) {
        if (cur.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) return cur;
        cur = cur.parent;
    }
    return cur;
}

/**
 * Checks wether the entity is a descendant of root
 * @param entity An arbitrary entity in the entityCollection
 * @param root The parent Entity to check against
 * @returns 
 */
const isDescendantOf = (entity: Entity, root: Entity) => {
    let cur: Entity | undefined = entity;
    while (cur) {
        if (cur === root) return true;
        cur = cur.parent;
    }
    return false;
}

/**
 * Makes a local ENU frame at given position. Handles entity position relativity
 * @param pos Cartesian3
 * @returns Matrix4
 */
function makeEnuFrameAt(pos: Cartesian3) {
    return Transforms.eastNorthUpToFixedFrame(pos);
}

/**
 * Returns all child entities in the same collection as parent
 * @param root Mastpoint
 * @param entityCollection Live or plan EntityCollection
 * @returns Entity[]
 */
const collectGroupEntities = (root: Entity, entityCollection: EntityCollection) => {
    const all = entityCollection?.values;
    const members: Entity[] = all?.filter(e => e !== root && isDescendantOf(e, root) && e.show) ?? [];

    return members;
}

/**
 * Takes in a supported Entity and clones then modifies its visual properties to another Entity
 * @param src Entity (Original/Source)
 * @param dst Entity (Ghost/Destination)
 */
const cloneVisuals = (src: Entity, dst: Entity) => {
    // Shallow clone common graphics if available.
    if (src.box && src.id.startsWith(ENTITY_ID_PREFIX.CROSSBEAM)) dst.box = src.box.clone?.() ?? src.box;
    if (src.cylinder && (src.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT) || src.id.startsWith(ENTITY_ID_PREFIX.POLE) )) dst.cylinder = src.cylinder.clone?.() ?? src.cylinder;
    if (src.polyline && src.id.startsWith(ENTITY_ID_PREFIX.UTILITYLINK)) dst.polyline = src.polyline.clone?.() ?? src.polyline;
    if (src.point && src.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) dst.point = src.point.clone?.() ?? src.point;

    // Add styling tweaks for preview “ghost”
    if (dst.box) dst.box.material = new ColorMaterialProperty(Color.GRAY.withAlpha(0.5));
    if (dst.cylinder) dst.cylinder.material = new ColorMaterialProperty(Color.GRAY.withAlpha(0.5));
    if (dst.point) {
        dst.point.color = new ConstantProperty(Color.YELLOW);
        dst.point.pixelSize = new ConstantProperty(4);
        dst.point.disableDepthTestDistance = new ConstantProperty(null);
        dst.point.scaleByDistance = new ConstantProperty(null);
    };
    if (dst.polyline) dst.polyline.material = new PolylineOutlineMaterialProperty({
        color: Color.GRAY.withAlpha(0.8),
        outlineColor: Color.BLACK,
        outlineWidth: 4
    });
}

/**
 * Creates the ghost preview entity in the reference datasource
 * @param src Entity to clone
 * @param refDatasource Datasource to use
 * @returns Ghost Entity
 */
function createPreviewEntity(src: Entity, refDatasource: CzmlDataSource) {
    if(src.show === false){
        return;
    }
    const e = refDatasource.entities.add({
        name: src.name,
        position: new ConstantPositionProperty(Cartesian3.ZERO),
        orientation: src.orientation, // recompute if needed
        show: true,
    });
    cloneVisuals(src, e);
    return e;
}

export { getGroupRoot, collectGroupEntities, createPreviewEntity, makeEnuFrameAt };